import apiClient from '../utils/apiClient';

/**
 * Sales Service
 * Handles all API calls related to sales invoices
 */
const salesService = {
  /**
   * Get all sales invoices with optional filtering
   * @param {Object} filters - Filter parameters
   * @param {Number} page - Page number for pagination
   * @param {Number} limit - Number of items per page
   * @returns {Promise} - Promise resolving to sales invoices data
   */
  getSalesInvoices: async (filters = {}, page = 1, limit = 10) => {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();

      // Add pagination
      queryParams.append('page', page);
      queryParams.append('limit', limit);

      // Add filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });

      const response = await apiClient.get(`/sales?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching sales invoices:', error);
      throw error.response?.data || { message: 'Error fetching sales invoices' };
    }
  },

  /**
   * Search for sales invoices
   * @param {String} searchQuery - Search query
   * @param {Number} page - Page number for pagination
   * @param {Number} limit - Number of items per page
   * @returns {Promise} - Promise resolving to search results
   */
  searchSalesInvoices: async (searchQuery, page = 1, limit = 10) => {
    try {
      const response = await apiClient.get(`/sales/search?contains=${encodeURIComponent(searchQuery.trim())}&page=${page}&limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Error searching sales invoices:', error);
      throw error.response?.data || { message: 'Error searching sales invoices' };
    }
  },

  /**
   * Get a sales invoice by invoice number
   * @param {String} invoiceNo - Sales invoice number
   * @returns {Promise} - Promise resolving to sales invoice data
   */
  getSalesInvoiceByInvoiceNo: async (invoiceNo, params = false) => {
    try {
      if (params) {
        const response = await apiClient.get(`/sales/${invoiceNo}?type=cn`);
        return response.data;
      }
      const response = await apiClient.get(`/sales/${invoiceNo}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching sales invoice ${invoiceNo}:`, error);
      throw error.response?.data || { message: 'Error fetching sales invoice' };
    }
  },

  /**
   * Get the next available sales invoice number
   * @returns {Promise} - Promise resolving to next sales invoice number
   */
  getNextSalesInvoiceNumber: async () => {
    try {
      const response = await apiClient.get('/sales/next-invoice-number');
      return response.data;
    } catch (error) {
      console.error('Error fetching next sales invoice number:', error);
      throw error.response?.data || { message: 'Error fetching next sales invoice number' };
    }
  },

  /**
   * Create a new sales invoice
   * @param {Object} salesData - Sales invoice data with master and detail information
   * @returns {Promise} - Promise resolving to created sales invoice
   */
  createSalesInvoice: async (salesData) => {
    try {
      const response = await apiClient.post('/sales', salesData);
      return response.data;
    } catch (error) {
      console.error('Error creating sales invoice:', error);
      throw error.response?.data || { message: 'Error creating sales invoice' };
    }
  },

  /**
   * Update an existing sales invoice
   * @param {String} invoiceNo - Sales invoice number
   * @param {Object} salesData - Updated sales invoice data
   * @returns {Promise} - Promise resolving to updated sales invoice
   */
  updateSalesInvoice: async (invoiceNo, salesData) => {
    try {
      const response = await apiClient.put(`/sales/${invoiceNo}`, salesData);
      return response.data;
    } catch (error) {
      console.error(`Error updating sales invoice ${invoiceNo}:`, error);
      throw error.response?.data || { message: 'Error updating sales invoice' };
    }
  },

  /**
   * Delete a sales invoice
   * @param {String} invoiceNo - Sales invoice number
   * @returns {Promise} - Promise resolving to deletion result
   */
  deleteSalesInvoice: async (invoiceNo) => {
    try {
      const response = await apiClient.delete(`/sales/${invoiceNo}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting sales invoice ${invoiceNo}:`, error);
      throw error.response?.data || { message: 'Error deleting sales invoice' };
    }
  },

  /**
   * Update sales invoice status
   * @param {String} invoiceNo - Sales invoice number
   * @param {String} status - New status
   * @returns {Promise} - Promise resolving to updated status
   */
  updateSalesInvoiceStatus: async (invoiceNo, status) => {
    try {
      const response = await apiClient.patch(`/sales/${invoiceNo}/status`, { status });
      return response.data;
    } catch (error) {
      console.error(`Error updating sales invoice status ${invoiceNo}:`, error);
      throw error.response?.data || { message: 'Error updating sales invoice status' };
    }
  }
};

export default salesService;
